<script setup lang="ts">
  import type { PropType } from 'vue'
  import { useVoiceWaveformCanvas } from '../composables/useVoiceWaveformCanvas'

  const props = defineProps({
    /** Stable DOM id for tests and aria wiring. Omit when multiple waveforms share a view. */
    id: {
      type: String,
    },
    /** Normalized 0..1 samples (decoded peaks or live amplitudes). */
    samples: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    /** Played fraction 0..1; bars before it render in the played color. */
    progress: {
      type: Number,
      default: 0,
    },
    /**
     * When true, render a right-aligned sliding window of recent samples
     * (active recording). When false, resample the full clip for playback.
     */
    live: {
      type: Boolean,
      default: false,
    },
    /** Bar width + gap in CSS px; bar count derives from the rendered width. */
    barWidth: {
      type: Number,
      default: 3,
    },
    barGap: {
      type: Number,
      default: 2,
    },
    height: {
      type: Number,
      default: 28,
    },
    playedColor: {
      type: String,
      default: 'currentColor',
    },
    unplayedColor: {
      type: String,
      default: 'var(--cp-border-600)',
    },
    /** When true, pointer emits a seek fraction (playback bubble only). */
    seekable: {
      type: Boolean,
      default: false,
    },
    /** Accessible name for the waveform (required for role=img / progressbar). Callers pass a translated label. */
    ariaLabel: {
      type: String,
      required: true,
    },
  })

  const emit = defineEmits<{ (e: 'seek', fraction: number): void }>()

  const { canvasRef, ariaValueNow, onPointer } = useVoiceWaveformCanvas({
    samples: () => props.samples,
    progress: () => props.progress,
    live: () => props.live,
    barWidth: () => props.barWidth,
    barGap: () => props.barGap,
    height: () => props.height,
    playedColor: () => props.playedColor,
    unplayedColor: () => props.unplayedColor,
    seekable: () => props.seekable,
    onSeek: fraction => emit('seek', fraction),
  })
</script>

<template>
  <!-- Seekable: pointer-only progressbar. Non-seekable: decorative img. -->
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <canvas
    :id="id"
    ref="canvasRef"
    data-voice-waveform
    class="voice-waveform"
    :style="{
      height: `${height}px`,
      width: '100%',
      cursor: seekable ? 'pointer' : 'default',
      color: playedColor,
    }"
    :role="seekable ? 'progressbar' : 'img'"
    :aria-label="ariaLabel"
    :aria-valuemin="seekable ? 0 : undefined"
    :aria-valuemax="seekable ? 100 : undefined"
    :aria-valuenow="seekable ? ariaValueNow : undefined"
    @pointerdown="onPointer"
  />
</template>

<style scoped>
  .voice-waveform {
    display: block;
    touch-action: none;
    overflow: hidden;
  }
</style>
